/**
 * RangeValueEditor  (SM-22 / DA-2295)
 *
 * The value control for the C (range) operator — shared shape for HP and, later, AP's
 * COUNT operator (§6: "range on read" for C is the same semantics). Mirrors
 * mapperSerializer's serializeRange exactly: `a-b` (upper-exclusive) when either bound
 * is set, otherwise a bare exact-equality value. Deliberately mutually exclusive in the
 * UI — filling one clears the other — so the builder can never produce the ambiguous
 * "both a range and a bare value" state the raw string grammar would silently allow.
 */
import React from 'react'
import { Box, TextField, Typography } from '@mui/material'

export interface RangeValue {
  value?: string
  rangeStart?: string
  rangeEnd?: string
}

export interface RangeValueEditorProps {
  value: RangeValue
  onChange: (value: RangeValue) => void
  disabled?: boolean
  /** DA-2184: the caller's "Value type" selector already chose Range — hide the exact-value field. */
  rangeOnly?: boolean
}

export const RangeValueEditor: React.FC<RangeValueEditorProps> = ({ value, onChange, disabled = false, rangeOnly = false }) => {
  const isRange = !!(value.rangeStart || value.rangeEnd)
  // `-b` alone uses Double.MIN_VALUE as the lower bound, so it silently excludes 0 —
  // a documented footgun (§3.4). Flag it rather than let it pass quietly.
  const openLowerBoundOnly = !!value.rangeEnd && !value.rangeStart

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {!rangeOnly && (
          <>
            <TextField
              label="Exact value"
              size="small"
              value={isRange ? '' : value.value ?? ''}
              onChange={(e) => onChange({ value: e.target.value, rangeStart: undefined, rangeEnd: undefined })}
              disabled={disabled || isRange}
              sx={{ width: 130 }}
            />
            <Typography variant="body2" color="text.secondary">or</Typography>
          </>
        )}
        <TextField
          label="From (≥)"
          size="small"
          value={value.rangeStart ?? ''}
          onChange={(e) => onChange({ value: undefined, rangeStart: e.target.value, rangeEnd: value.rangeEnd })}
          disabled={disabled}
          sx={{ width: 110 }}
        />
        <TextField
          label="To (<)"
          size="small"
          value={value.rangeEnd ?? ''}
          onChange={(e) => onChange({ value: undefined, rangeStart: value.rangeStart, rangeEnd: e.target.value })}
          disabled={disabled}
          sx={{ width: 110 }}
          slotProps={openLowerBoundOnly ? { htmlInput: { 'aria-describedby': 'range-open-lower-warning' } } : undefined}
        />
      </Box>
      {openLowerBoundOnly && (
        // role="alert" so the warning is announced the moment it appears (keyboard/SR users get
        // the same cue a sighted user does), and it's aria-describedby-linked to the "To" field.
        <Typography id="range-open-lower-warning" role="alert" variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
          "To" alone excludes 0 as a lower bound — set "From" too if 0 should match.
        </Typography>
      )}
    </Box>
  )
}
